import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import Paginator from '../../components/Paginator';
import { Button } from '../../components/ui/button';
import { DatePicker } from '../../components/ui/date-picker';
import { SearchInput } from '../../components/SearchInput';
import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
    ComboboxTrigger,
    ComboboxValue,
} from '../../components/ui/combobox';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { useTrans } from '../../hooks/useTrans';
import { formatDateShort, formatMoney } from '../../lib/formatters';

export default function InvoiceIndex({ invoices, years, filters }) {
    const translate = useTrans();
    const [form, setForm] = useState({
        start_date: filters.startDate || '',
        end_date: filters.endDate || '',
        year: filters.year || '',
        query: filters.query || '',
        order_column: filters.orderColumn || 'date',
        order_direction: filters.orderDirection || 'desc',
    });

    function handleSubmit(e) {
        e.preventDefault();
        router.get(window.location.pathname, form, { preserveState: true });
    }

    function handleChange(e) {
        setForm({ ...form, [e.target.name]: e.target.value });
    }

    function handleSort(column) {
        const direction = form.order_column === column && form.order_direction === 'asc' ? 'desc' : 'asc';
        const newForm = { ...form, order_column: column, order_direction: direction };
        setForm(newForm);
        router.get(window.location.pathname, newForm, { preserveState: true });
    }

    function SortableHead({ column, children }) {
        const isActive = form.order_column === column;
        const arrow = isActive ? (form.order_direction === 'asc' ? ' ↑' : ' ↓') : '';
        return (
            <TableHead
                className="py-5 px-2.5 text-[#003545] font-normal text-sm cursor-pointer select-none hover:text-[#0e8e6a]"
                onClick={() => handleSort(column)}
            >
                <span className="underline">{children}</span>{arrow}
            </TableHead>
        );
    }

    const hasInvoices = invoices && invoices.data && invoices.data.length > 0;

    return (
        <div className="max-w-[1200px] mx-auto px-4">
            <div className="bg-white rounded-[3px] border border-[#bbb]">
                <div className="p-[30px] pb-0">
                    <h3 className="text-2xl font-bold text-[#003545] mb-6">{translate('Factuuroverzicht')}</h3>

                    <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-2 mb-6">
                        <label className="flex items-center gap-1 text-sm font-bold text-[#003545]">
                            {translate('Van')}
                            <DatePicker name="start_date" value={form.start_date} onChange={handleChange} captionLayout="dropdown" startYear={2015} endYear={new Date().getFullYear()} />
                        </label>
                        <label className="flex items-center gap-1 text-sm font-bold text-[#003545]">
                            {translate('Tot')}
                            <DatePicker name="end_date" value={form.end_date} onChange={handleChange} captionLayout="dropdown" startYear={2015} endYear={new Date().getFullYear()} />
                        </label>
                        <label className="flex items-center gap-1 text-sm font-bold text-[#003545]">
                            {translate('Jaar')}
                            <Combobox
                                items={[translate('Toon alles'), ...years.map(year => String(year))]}
                                value={form.year || translate('Toon alles')}
                                onValueChange={(value) => setForm({ ...form, year: value === translate('Toon alles') ? '' : (value ?? '') })}
                            >
                                <ComboboxTrigger render={
                                    <Button variant="outline" className="w-28 font-normal justify-between rounded-sm border-[#bbb] shadow-none">
                                        <ComboboxValue placeholder={translate('Toon alles')} />
                                    </Button>
                                } />
                                <ComboboxContent>
                                    <ComboboxInput showTrigger={false} placeholder={translate('Zoek jaar...')} />
                                    <ComboboxEmpty>{translate('Geen jaren gevonden.')}</ComboboxEmpty>
                                    <ComboboxList>
                                        {(year) => (
                                            <ComboboxItem key={year} value={year}>
                                                {year}
                                            </ComboboxItem>
                                        )}
                                    </ComboboxList>
                                </ComboboxContent>
                            </Combobox>
                        </label>
                        <div className="flex items-center gap-2 ml-auto">
                            <SearchInput
                                name="query"
                                value={form.query}
                                onChange={handleChange}
                                onClear={() => {
                                    const newForm = { ...form, query: '' };
                                    setForm(newForm);
                                    router.get(window.location.pathname, newForm, { preserveState: true });
                                }}
                                showClear={!!filters.query}
                                placeholder={translate('Factuurnummer of debiteur')}
                                className="w-60"
                            />
                            <Button variant="primary" type="submit">{translate('Zoeken')}</Button>
                        </div>
                    </form>
                </div>

                <div className="px-[30px] pb-[30px]">
                    <Table>
                        <TableHeader className="bg-[#ededed] [&_tr]:border-b-2 [&_tr]:border-[#d4d4d4]">
                            <TableRow>
                                <SortableHead column="date">{translate('Datum')}</SortableHead>
                                <SortableHead column="invoice_number">{translate('Factuurnummer')}</SortableHead>
                                <SortableHead column="debtor">{translate('Debiteur')}</SortableHead>
                                <SortableHead column="amount">{translate('Bedrag')}</SortableHead>
                                <TableHead className="py-5 px-2.5 text-[#003545] font-normal text-sm">{translate('Downloaden')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {hasInvoices ? (
                                invoices.data.map((invoice, index) => (
                                    <TableRow key={invoice.number} className={index % 2 === 1 ? 'bg-[#f5f5f5]' : ''}>
                                        <TableCell className="py-5 px-2.5 whitespace-nowrap">{formatDateShort(invoice.date)}</TableCell>
                                        <TableCell className="py-5 px-2.5">{invoice.number}</TableCell>
                                        <TableCell className="py-5 px-2.5">{invoice.debtor?.company_name}</TableCell>
                                        <TableCell className="py-5 px-2.5">&euro; {formatMoney(invoice.amount)}</TableCell>
                                        <TableCell className="py-5 px-2.5">
                                            <Button variant="clear" asChild><a href={`/facturen/${invoice.number}/downloaden`}>{translate('Download PDF')}</a></Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} className="py-5 px-2.5 text-center text-gray-500">
                                        {translate('Er zijn geen facturen gevonden.')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>

                    <Paginator links={invoices.links} />
                </div>
            </div>
        </div>
    );
}

